import { Children, useCallback } from 'react';

import PropTypes from 'prop-types';

import Heading from '@zenbusiness/zui/Heading';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';

import Card from './Card';
import Divider from './Divider';
import EditButton from './EditButton';
import SectionNumber from './SectionNumber';
import { useCheckout } from '../../hooks/useCheckout';

function CheckoutCard({
  alwaysEdit,
  children,
  hideEdit,
  sectionKey,
  sectionNumber,
  subtitle,
  title
}) {
  const { validSections, editingSections, editSection } = useCheckout();
  const isValid = validSections[sectionKey];
  const isEditing = editingSections[sectionKey] || alwaysEdit;
  const showEditButton = isValid && !isEditing && !hideEdit;

  const handleEdit = useCallback(() => {
    editSection(sectionKey);
  }, [editSection, sectionKey]);

  // Filter children based on card state (validity and editing status)
  // Note: If props need to be passed to children in the future, use
  // React.cloneElement(child, childProps) instead of returning the child directly.
  const renderChildren = () => {
    return Children.map(children, (child) => {
      // Skip non-elements
      if (!child || typeof child.type === 'string') {
        return child;
      }

      // Summary component should render when valid and not editing
      if (child.type === CheckoutCard.Summary) {
        return !isEditing && isValid ? child : null;
      }

      // Edit component should render when editing or not valid
      if (child.type === CheckoutCard.Edit) {
        return isEditing || !isValid ? child : null;
      }

      return child;
    });
  };

  return (
    <Card component="section" data-testid={`${sectionKey}-card`}>
      <Stack
        alignItems="center"
        component="header"
        direction="row"
        justifyContent="space-between"
        spacing={1}
      >
        <Stack alignItems="center" direction="row" spacing={1.5}>
          <SectionNumber isValid={isValid && !alwaysEdit} number={sectionNumber} />
          <Stack>
            <Heading component="h3" size="lg" weight="medium">
              {title}
            </Heading>
            {subtitle && (
              <Text color="orange" size="sm">
                {subtitle}
              </Text>
            )}
          </Stack>
        </Stack>

        {showEditButton && <EditButton sectionKey={sectionKey} onClick={handleEdit} />}
      </Stack>
      <Divider />
      {renderChildren()}
    </Card>
  );
}

CheckoutCard.propTypes = {
  alwaysEdit: PropTypes.bool,
  children: PropTypes.node.isRequired,
  hideEdit: PropTypes.bool,
  sectionKey: PropTypes.string.isRequired,
  sectionNumber: PropTypes.number.isRequired,
  subtitle: PropTypes.node,
  title: PropTypes.node.isRequired
};

export default CheckoutCard;
